/**
 * Custom domains — apps as data (Data Design PRD §6.4) — client↔server contract.
 *
 * The composer at /settings/workspace/domains sends INTENT (a mode + name +
 * member kinds + pinned saved-view uids + a home view); the route validates it
 * in core (mode/kind/view refs) and writes a `custom_domains` row. Enabled
 * domains appear in the mode sub-nav via the registry merge (soft-nav safe).
 */

import { z } from "zod";
import type { Operation } from "../../types";

export const domainModeSchema = z.enum(["work", "customers", "product", "operations", "agents"]);
export type DomainModeDto = z.infer<typeof domainModeSchema>;

export const customDomainRowSchema = z.object({
  uid: z.string(),
  slug: z.string(),
  name: z.string(),
  icon: z.string().nullable(),
  mode: z.string(),
  member_kinds: z.array(z.string()),
  pinned_view_uids: z.array(z.string()),
  home_view_uid: z.string().nullable(),
  enabled: z.boolean(),
  sort_order: z.number().nullable(),
  origin: z.enum(["user", "agent", "system"]),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type CustomDomainRowDto = z.infer<typeof customDomainRowSchema>;

// ---------- list ----------

export const domainsListResponse = z.object({ domains: z.array(customDomainRowSchema) });
// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
export const domainsList: Operation<void, void, void, z.infer<typeof domainsListResponse>> = {
  method: "GET",
  path: () => "/api/settings/domains",
  response: domainsListResponse,
};

// ---------- create ----------

export const domainCreateBody = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  icon: z.string().nullable().optional(),
  mode: domainModeSchema,
  member_kinds: z.array(z.string()).optional(),
  pinned_view_uids: z.array(z.string()).optional(),
  home_view_uid: z.string().nullable().optional(),
});
export type DomainCreateBody = z.infer<typeof domainCreateBody>;
export const domainCreateResponse = z.object({ domain: customDomainRowSchema });
// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
export const domainsCreate: Operation<
  void,
  void,
  z.infer<typeof domainCreateBody>,
  z.infer<typeof domainCreateResponse>
> = {
  method: "POST",
  path: () => "/api/settings/domains",
  body: domainCreateBody,
  response: domainCreateResponse,
  invalidates: [domainsList],
};

// ---------- update (rename / re-home / toggle enabled / recompose) ----------

export const domainUpdateBody = z.object({
  slug: z.string().min(1),
  name: z.string().optional(),
  icon: z.string().nullable().optional(),
  mode: domainModeSchema.optional(),
  member_kinds: z.array(z.string()).optional(),
  pinned_view_uids: z.array(z.string()).optional(),
  home_view_uid: z.string().nullable().optional(),
  enabled: z.boolean().optional(),
});
export type DomainUpdateBody = z.infer<typeof domainUpdateBody>;
export const domainUpdateResponse = z.object({ domain: customDomainRowSchema });
// coverage-ignore: Hono port deferred after RO-10-09 Next delete; contract retained for apiKey
export const domainsUpdate: Operation<
  void,
  void,
  z.infer<typeof domainUpdateBody>,
  z.infer<typeof domainUpdateResponse>
> = {
  method: "PATCH",
  path: () => "/api/settings/domains",
  body: domainUpdateBody,
  response: domainUpdateResponse,
  invalidates: [domainsList],
};

export const domains = {
  list: domainsList,
  create: domainsCreate,
  update: domainsUpdate,
};
